import React, { useState, useEffect, useContext } from "react";
import languageContext from "../context/language/languageContext";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

const InsuranceDashboard = () => {
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const { setSessionTimeout, setAlert, setMessage } = useContext(languageContext);

  const navigate = useNavigate();

  // ✅ Fetch patient details (only once)
  const getPatientDetails = async () => {
    const headersList = {
      Accept: "*/*",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      "Content-Type": "application/json",
    };
    try {
      setLoading(true);
      const response = await axios.post(
        "https://www.careplus.gopillz.com/insurance_patient_details",
        {},
        { headers: headersList }
      );
      if (response.status === 200) {
        setPatients(response.data?.data || []);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  // ❌ Handle error
  const handleError = (error) => {
    setLoading(false);
    if (error.response?.data?.detail === "Token has expired") {
      setSessionTimeout(true);
    } else {
      setAlert(true);
      setMessage("Something went wrong. Please try again.");
    }
  };

  // 🔄 Init load
  useEffect(() => {
    const init = async () => {
      const currentPagePath = window.location.pathname;
      const loginBool = localStorage.getItem("login");

      if (loginBool === "true") {
        navigate("/insurance-dashboard", { state: { url: currentPagePath } });
        await getPatientDetails();
      } else {
        navigate("/login", { state: { url: currentPagePath } });
      }
    };
    init();
  }, []);

  const handleView = (path, patient_id) =>
    navigate(path, { state: { patient_id } });

  // ✅ Function to calculate age from date_of_birth (DD/MM/YYYY)
  function calculateAge(dobString) {
    if (!dobString) return null;

    const [day, month, year] = dobString.split("/").map(Number);
    const dob = new Date(year, month - 1, day); // month is 0-based

    const today = new Date();
    let years = today.getFullYear() - dob.getFullYear();
    let months = today.getMonth() - dob.getMonth();
    let days = today.getDate() - dob.getDate();

    if (days < 0) {
      months -= 1;
      days += new Date(today.getFullYear(), today.getMonth(), 0).getDate();
    }

    if (months < 0) {
      years -= 1;
      months += 12;
    }

    return { years, months };
  }

  // ✅ Frontend filter
  const filteredPatients = patients.filter((patient) => {
    const search = searchTerm.toLowerCase();
    return (
      patient.name?.toLowerCase().includes(search) ||
      patient.uid?.toString().includes(search) ||
      patient.phone_number?.toString().includes(search)
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-tr from-indigo-50 to-purple-100">
      <Navbar />

      <div className="container mx-auto px-4 pt-28 pb-16">
        <h1 className="text-4xl font-bold text-indigo-800 mb-6 text-center">
          🏥 Insurance Dashboard – Patient Insights
        </h1>
        <p className="text-center text-gray-600 mb-10">
          Track patient health, bookings, and medical analysis reports.
        </p>

        {/* Search */}
        <div className="flex justify-center mb-8">
          <input
            type="text"
            placeholder="🔍 Search by Name, UID, or Phone"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full max-w-md px-5 py-2 rounded-xl border border-indigo-300 shadow focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* Patient Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-2">
          {loading ? (
            <div className="col-span-full flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600 border-b-4"></div>
            </div>
          ) : filteredPatients.length === 0 ? (
            <p className="text-center text-gray-600 col-span-full">
              No patients found.
            </p>
          ) : (
            filteredPatients.map((patient) => {
              const age = calculateAge(patient.date_of_birth);
              return (
                <div
                  key={patient.id}
                  className="bg-white rounded-2xl p-6 border border-indigo-200 shadow hover:shadow-xl transition transform hover:scale-[1.01] flex flex-col"
                >
                  {/* Card Content */}
                  <div className="flex-grow">
                    <h2 className="text-xl font-semibold text-indigo-800">
                      {patient.name}
                    </h2>
                    <p className="text-gray-600 text-sm mt-1">📱 {patient.phone_number}</p>
                    <p className="text-gray-600 text-sm">
                      🎂 Age: {age ? `${age.years} years ${age.months} months` : "N/A"}
                    </p>
                    <p className="text-gray-600 text-sm">⚧ Gender: {patient.gender}</p>
                    <p className="text-gray-600 text-sm">🆔 UID: {patient.uid}</p>
                  </div>

                  {/* Action Buttons → always aligned at bottom */}
                  <div className="mt-5 grid grid-cols-1 gap-2">
                    {/* Summary */}
                    <button
                      onClick={() => handleView("/chat_summary", patient.id)}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg text-sm"
                    >
                      📄 Patient Summary
                    </button>

                    {/* Appointments */}
                    <button
                      onClick={() => handleView("/patient_appointments", patient.id)}
                      className="w-full bg-teal-500 hover:bg-teal-600 text-white py-2 rounded-lg text-sm"
                    >
                      📅 Appointments
                    </button>

                    {/* Lab Tests */}
                    <button
                      onClick={() => handleView("/patient_lab_tests", patient.id)}
                      className="w-full bg-purple-500 hover:bg-purple-600 text-white py-2 rounded-lg text-sm"
                    >
                      🧪 Lab Tests
                    </button>

                    {/* Medicines */}
                    <button
                      onClick={() => handleView("/patient_medicines", patient.id)}
                      className="w-full bg-green-500 hover:bg-green-600 text-white py-2 rounded-lg text-sm"
                    >
                      💊 Medicines
                    </button>

                    {/* Adherence Report */}
                    <button
                      onClick={() => handleView("/adherence_report", patient.id)}
                      className="w-full bg-pink-500 hover:bg-pink-600 text-white py-2 rounded-lg text-sm"
                    >
                      ⏰ Adherence Report
                    </button>
                  </div>

                </div>

              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default InsuranceDashboard;
